/**
 * Facility shift schedule — JavaScript mirror of app/models/shift.rb.
 *
 * Canonical shift definition (wall-clock times in the FACILITY time zone,
 * never the browser's zone):
 *
 *   1st shift: 07:00-14:59
 *   2nd shift: 15:00-22:59
 *   3rd shift: 23:00-06:59 (spans midnight; anchored to the date it began)
 *
 * Times in the 00:00-06:59 range belong to the 3rd shift of the PREVIOUS
 * calendar date. Boundary instants are derived through the Intl API pinned to
 * FACILITY_TIME_ZONE, so DST transitions are handled correctly (the 3rd shift
 * is 7 real hours on the spring-forward night and 9 real hours on the
 * fall-back night).
 *
 * A shift is represented as a plain object: { date: "YYYY-MM-DD", number: 1|2|3 }.
 *
 * Both this module and app/models/shift.rb are pinned by the shared golden
 * fixture at test/fixtures/files/shift_golden_cases.json — keep them in sync.
 *
 * Authored as CommonJS so webpack import sites and Node's test runner
 * (node --test test/javascript/) can both load it.
 */

const FACILITY_TIME_ZONE = "America/Toronto";

const FIRST_START_HOUR = 7;
const SECOND_START_HOUR = 15;
const THIRD_START_HOUR = 23;

const START_HOURS = {
  1: FIRST_START_HOUR,
  2: SECOND_START_HOUR,
  3: THIRD_START_HOUR
};

const NUMBER_LABELS = { 1: "1st", 2: "2nd", 3: "3rd" };

// Cached Intl formatter — creating one is expensive; formatting is cheap.
let facilityFormatter = null;

function getFacilityFormatter() {
  if (!facilityFormatter) {
    facilityFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: FACILITY_TIME_ZONE,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  }
  return facilityFormatter;
}

function toInstant(dateLike) {
  return dateLike instanceof Date ? dateLike : new Date(dateLike);
}

/**
 * Render an instant as facility-local wall-clock fields.
 * @param {Date} instant
 * @returns {{year: number, month: number, day: number, hour: number, minute: number, second: number}}
 */
function facilityWallClock(instant) {
  const fields = {};
  getFacilityFormatter()
    .formatToParts(instant)
    .forEach((part) => {
      if (part.type !== "literal") fields[part.type] = parseInt(part.value, 10);
    });
  return fields;
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

function formatDate(year, month, day) {
  return `${String(year).padStart(4, "0")}-${pad2(month)}-${pad2(day)}`;
}

function parseDateString(dateString) {
  const [year, month, day] = dateString.split("-").map(Number);
  return { year, month, day };
}

function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year, month) {
  const lengths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (month === 2 && isLeapYear(year)) return 29;
  return lengths[month - 1];
}

/**
 * Pure calendar arithmetic on Y/M/D integers — no Date-object day math in
 * local time, so it is immune to the browser zone and DST.
 * @param {string} dateString "YYYY-MM-DD"
 * @param {number} delta whole days (may be negative)
 * @returns {string} "YYYY-MM-DD"
 */
function addDays(dateString, delta) {
  let { year, month, day } = parseDateString(dateString);
  day += delta;
  while (day > daysInMonth(year, month)) {
    day -= daysInMonth(year, month);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  while (day < 1) {
    month -= 1;
    if (month < 1) {
      month = 12;
      year -= 1;
    }
    day += daysInMonth(year, month);
  }
  return formatDate(year, month, day);
}

/**
 * Classify a facility-local wall-clock hour into a shift number.
 * @param {number} hour 0-23
 * @returns {1|2|3}
 */
function shiftNumberForHour(hour) {
  if (hour >= FIRST_START_HOUR && hour < SECOND_START_HOUR) return 1;
  if (hour >= SECOND_START_HOUR && hour < THIRD_START_HOUR) return 2;
  return 3;
}

/**
 * Build the shift containing a facility-local wall-clock time. 00:00-06:59
 * belongs to the 3rd shift that began the previous date.
 * @param {number} year
 * @param {number} month 1-12
 * @param {number} day
 * @param {number} hour 0-23
 * @returns {{date: string, number: number}}
 */
function shiftFromWallClock(year, month, day, hour) {
  const number = shiftNumberForHour(hour);
  const date = formatDate(year, month, day);
  if (number === 3 && hour < FIRST_START_HOUR) {
    return { date: addDays(date, -1), number: 3 };
  }
  return { date, number };
}

/**
 * Pure classification of an instant in time into the shift that contains it.
 * @param {Date|string|number} dateLike anything `new Date` accepts
 * @returns {{date: string, number: number}}
 */
function shiftAt(dateLike) {
  const local = facilityWallClock(toInstant(dateLike));
  return shiftFromWallClock(local.year, local.month, local.day, local.hour);
}

/**
 * The real time, or the QA-simulated time when one is pinned (non-production
 * environments set window.__SIMULATED_NOW__ from the server).
 * @returns {Date}
 */
function realOrSimulatedNow() {
  if (typeof window !== "undefined" && window.__SIMULATED_NOW__) {
    const simulated = new Date(window.__SIMULATED_NOW__);
    if (!Number.isNaN(simulated.getTime())) return simulated;
  }
  return new Date();
}

/**
 * The shift containing `now` (defaults to the current, or simulated, time).
 * @param {Date} [now]
 * @returns {{date: string, number: number}}
 */
function currentShift(now = realOrSimulatedNow()) {
  return shiftAt(now);
}

/**
 * @param {number} number 1|2|3
 * @returns {string} "1st" | "2nd" | "3rd"
 */
function shiftNumberLabel(number) {
  return NUMBER_LABELS[number];
}

/**
 * @param {number} number 1|2|3
 * @returns {string} "Shift: 1st" style label
 */
function shiftLabel(number) {
  return `Shift: ${shiftNumberLabel(number)}`;
}

/**
 * The next shift (3rd -> 1st of the next date).
 * @param {{date: string, number: number}} shift
 * @returns {{date: string, number: number}}
 */
function succ(shift) {
  if (shift.number === 3) return { date: addDays(shift.date, 1), number: 1 };
  return { date: shift.date, number: shift.number + 1 };
}

/**
 * The previous shift (1st -> 3rd of the previous date).
 * @param {{date: string, number: number}} shift
 * @returns {{date: string, number: number}}
 */
function prev(shift) {
  if (shift.number === 1) return { date: addDays(shift.date, -1), number: 3 };
  return { date: shift.date, number: shift.number - 1 };
}

/**
 * Convert a facility wall-clock time to an instant without a tz library.
 * Standard two-pass zone inversion: start from the UTC candidate, then adjust
 * by the difference between the candidate's facility-local rendering and the
 * target wall time. Shift boundary hours (07/15/23) are never inside the DST
 * gap/fold, so this converges exactly within the fixed iteration budget.
 * @returns {Date}
 */
function wallClockToInstant(year, month, day, hour) {
  const target = Date.UTC(year, month - 1, day, hour, 0, 0, 0);
  let timestamp = target;
  for (let pass = 0; pass < 3; pass++) {
    const local = facilityWallClock(new Date(timestamp));
    const rendered = Date.UTC(
      local.year,
      local.month - 1,
      local.day,
      local.hour,
      local.minute,
      local.second,
      0
    );
    if (rendered === target) break;
    timestamp += target - rendered;
  }
  return new Date(timestamp);
}

/**
 * The instant the shift begins in the facility zone.
 * @param {{date: string, number: number}} shift
 * @returns {Date}
 */
function startsAt(shift) {
  const { year, month, day } = parseDateString(shift.date);
  return wallClockToInstant(year, month, day, START_HOURS[shift.number]);
}

/**
 * The instant the shift ends (exclusive) — the start of the next shift.
 * @param {{date: string, number: number}} shift
 * @returns {Date}
 */
function endsAt(shift) {
  return startsAt(succ(shift));
}

/**
 * Whether the shift contains the instant: startsAt <= t < endsAt.
 * @param {{date: string, number: number}} shift
 * @param {Date|string|number} dateLike
 * @returns {boolean}
 */
function cover(shift, dateLike) {
  const timestamp = toInstant(dateLike).getTime();
  return startsAt(shift).getTime() <= timestamp && timestamp < endsAt(shift).getTime();
}

/**
 * Chronological comparison by (date, number).
 * @returns {number} negative, zero, or positive
 */
function compareShifts(a, b) {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  if (a.number !== b.number) return a.number < b.number ? -1 : 1;
  return 0;
}

module.exports = {
  FACILITY_TIME_ZONE,
  FIRST_START_HOUR,
  SECOND_START_HOUR,
  THIRD_START_HOUR,
  START_HOURS,
  addDays,
  shiftNumberForHour,
  shiftFromWallClock,
  shiftAt,
  realOrSimulatedNow,
  currentShift,
  shiftNumberLabel,
  shiftLabel,
  succ,
  prev,
  startsAt,
  endsAt,
  cover,
  compareShifts
};
